import {
  ASSISTANT_TERMINAL_SCREEN_MAX,
  ASSISTANT_TERMINAL_SELECTION_MAX,
  AssistantAttachment,
  AssistantConversation,
  AssistantConversationList,
  AssistantConversationSummary,
  Problem,
  redactText,
  type AssistantCard,
  type AssistantContextInput,
  type AssistantMessage,
  type AssistantPageContext,
  type AssistantStreamEvent,
  type AssistantTerminalContext,
  type CreateAssistantAttachmentRequest,
} from "@cloudserver/contracts";
import i18n from "i18next";
import { useSyncExternalStore } from "react";
import { api, ApiProblem, API_BASE } from "../api/client.js";
import { prepareFile, type PendingAttachment } from "./attachments.js";
import type { PageInfo } from "./page.js";
import type { Screen } from "./screens.js";
import { whereToOpen } from "./selection.js";
import { createSseParser } from "./sse.js";

/*
 * The chat's state (docs/assistant.md): open or closed, docked or expanded,
 * the conversations and their messages, answers streaming in. A plain store
 * outside React, like the terminal sessions (`terminal/store.ts`), so the
 * window keeps its state and answers keep arriving while the person moves
 * through the panel.
 */

export type View = "docked" | "expanded" | "settings";

export interface Thread {
  messages: AssistantMessage[];
  loaded: boolean;
  running: boolean;
  /** Why the last message got no answer: a refusal before it ran (limits) or a failed stream. */
  problem: string | null;
}

export interface AssistantState {
  open: boolean;
  view: View;
  /** Width of the docked panel in pixels, remembered per browser. */
  width: number;
  activeId: string | null;
  conversations: AssistantConversationSummary[];
  threads: Record<string, Thread>;
  /** Conversations whose answer arrived while the person was not looking at them. */
  unread: string[];
  /** The conversation list was read at least once (the server page's count needs it). */
  listed: boolean;
  /** A first question to put into the composer of a conversation («Спросить ассистента»). */
  drafts: Record<string, string>;
  /** Attachments waiting in a conversation's composer. */
  pending: Record<string, PendingAttachment[]>;
  /** The conversation list shows only those about this resource (a server page's «Ассистент»). */
  filter: { kind: AssistantContextInput["kind"]; resourceId: string; label: string } | null;
}

export const WIDTH = { min: 400, max: 560, initial: 440 } as const;
const WIDTH_KEY = "cs.assistant.width";

function stored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private windows may refuse storage; the default comes back next time.
  }
}

export const clampWidth = (width: number): number =>
  Math.min(WIDTH.max, Math.max(WIDTH.min, Math.round(width)));

const EMPTY_THREAD: Thread = { messages: [], loaded: false, running: false, problem: null };

/**
 * The thread after one event of an answer; `question` is the text the person
 * sent and `attachments` what went along with it.
 */
export function applyEvent(
  thread: Thread,
  event: AssistantStreamEvent,
  question: string,
  attachments: AssistantMessage["attachments"] = [],
): Thread {
  const messages = thread.messages;
  const last = messages.at(-1);
  const updateAnswer = (change: (m: AssistantMessage) => AssistantMessage): Thread =>
    last?.role === "assistant"
      ? { ...thread, messages: [...messages.slice(0, -1), change(last)] }
      : thread;
  switch (event.type) {
    case "start": {
      const now = new Date().toISOString();
      return {
        ...thread,
        running: true,
        problem: null,
        messages: [
          ...messages,
          {
            id: event.userMessageId,
            role: "user",
            // After a card was decided the "message" is its outcome, which the card shows.
            text: event.cardOutcome || event.runCommand ? "" : question,
            status: "complete",
            refusal: null,
            tools: [],
            cards: [],
            cardOutcome: event.cardOutcome ?? null,
            runCommand: event.runCommand ?? null,
            attachments,
            focusChange: null,
            model: null,
            createdAt: now,
          },
          {
            id: event.messageId,
            role: "assistant",
            text: "",
            status: "complete",
            refusal: null,
            tools: [],
            cards: [],
            cardOutcome: null,
            attachments: [],
            focusChange: null,
            model: null,
            createdAt: now,
          },
        ],
      };
    }
    case "card":
      return withCard(thread, event.card);
    case "text":
      return updateAnswer((m) => ({ ...m, text: m.text + event.delta }));
    case "replace":
      // What the model wrote before a tool call was narration: the answer drops it.
      return updateAnswer((m) => ({ ...m, text: event.text }));
    case "tool":
      return updateAnswer((m) => {
        const known = m.tools.some((t) => t.id === event.tool.id);
        return {
          ...m,
          tools: known
            ? m.tools.map((t) => (t.id === event.tool.id ? event.tool : t))
            : [...m.tools, event.tool],
        };
      });
    case "refusal":
      return updateAnswer((m) => ({ ...m, refusal: event.refusal }));
    case "error":
      return { ...thread, problem: event.reason };
    case "done":
      return { ...updateAnswer((m) => ({ ...m, status: event.status })), running: false };
    case "context":
      return thread;
  }
}

/**
 * A card proposed (added to its answer) or changed (replaced where it is):
 * cards live on the answer that proposed them, whatever answer is streaming now.
 */
export function withCard(thread: Thread, card: AssistantCard): Thread {
  let found = false;
  const messages = thread.messages.map((m) => {
    if (!m.cards.some((c) => c.id === card.id)) return m;
    found = true;
    return { ...m, cards: m.cards.map((c) => (c.id === card.id ? card : c)) };
  });
  if (found) return { ...thread, messages };
  const at = messages.findIndex((m) => m.id === card.messageId);
  const index = at >= 0 ? at : messages.findLastIndex((m) => m.role === "assistant");
  if (index < 0) return thread;
  return {
    ...thread,
    messages: messages.map((m, i) => (i === index ? { ...m, cards: [...m.cards, card] } : m)),
  };
}

/** How many of the person's conversations have a card waiting for them («ждёт вас»). */
export const waitingCount = (conversations: readonly AssistantConversationSummary[]): number =>
  conversations.filter((c) => c.waiting > 0).length;

/** A chip the assistant attached, added to the conversation's summary once. */
export function withChip(
  summary: AssistantConversationSummary,
  item: AssistantConversationSummary["context"][number],
): AssistantConversationSummary {
  return summary.context.some((c) => c.id === item.id)
    ? summary
    : { ...summary, context: [...summary.context, item] };
}

export interface NewConversation {
  projectId: string | null;
  title: string;
  chip: AssistantContextInput | null;
  /** A first question, put into the composer for the person to send or change. */
  question?: string;
  /** Text to attach (a terminal's selection). */
  text?: { name: string; text: string; source: "paste" | "terminal"; serverId?: string };
}

/** The reasons of the ▶ route's own 404s: the route is there, the block or the session is not. */
const RUN_REASONS: ReadonlySet<string> = new Set([
  "assistant_message_not_found",
  "assistant_conversation_not_found",
  "terminal_session_not_found",
  "assistant_unavailable",
]);

/** Where the person is: their time zone and the panel's language, for an answer they did not type. */
const placeOfPerson = () => ({
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  language: i18n.language.startsWith("ru") ? ("ru" as const) : ("en" as const),
});

const reasonOf = async (response: Response): Promise<string> => {
  const parsed = Problem.safeParse(await response.json().catch(() => null));
  return parsed.success ? (parsed.data.reason ?? parsed.data.code) : "assistant_failed";
};

export class AssistantStore {
  private state: AssistantState;
  private readonly listeners = new Set<() => void>();

  constructor(initial: Partial<AssistantState> = {}) {
    this.state = {
      open: false,
      view: "docked",
      width: clampWidth(Number(stored(WIDTH_KEY)) || WIDTH.initial),
      activeId: null,
      conversations: [],
      threads: {},
      unread: [],
      listed: false,
      drafts: {},
      pending: {},
      filter: null,
      ...initial,
    };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly snapshot = (): AssistantState => this.state;

  private set(next: Partial<AssistantState>): void {
    this.state = { ...this.state, ...next };
    for (const listener of this.listeners) listener();
  }

  thread(id: string | null): Thread {
    return (id && this.state.threads[id]) || EMPTY_THREAD;
  }

  private setThread(id: string, change: (thread: Thread) => Thread): void {
    this.set({ threads: { ...this.state.threads, [id]: change(this.thread(id)) } });
  }

  private setSummary(summary: AssistantConversationSummary): void {
    const others = this.state.conversations.filter((c) => c.id !== summary.id);
    this.set({ conversations: [summary, ...others] });
  }

  close(): void {
    this.set({ open: false, view: this.state.view === "settings" ? "docked" : this.state.view });
  }

  setView(view: View): void {
    this.set({ view, open: true });
  }

  setWidth(width: number): void {
    const next = clampWidth(width);
    store(WIDTH_KEY, String(next));
    this.set({ width: next });
  }

  /** Shows a conversation; the answer that arrived in it is now seen. */
  select(id: string): void {
    this.set({ activeId: id, unread: this.state.unread.filter((u) => u !== id) });
    if (!this.thread(id).loaded) void this.load(id);
  }

  async refreshList(): Promise<void> {
    const list = await api("/assistant/conversations", { schema: AssistantConversationList });
    this.set({ conversations: list, listed: true });
  }

  // --- «Спросить ассистента», the server page's filter, screens -----------------------

  /** Opens the chat about something the person points at, with a first question ready. */
  async ask(page: PageInfo, fresh: NewConversation): Promise<void> {
    await this.openFor(page, fresh);
    const id = this.state.activeId;
    if (!id) return;
    if (fresh.question) this.set({ drafts: { ...this.state.drafts, [id]: fresh.question } });
    if (fresh.text) await this.attachText(id, fresh.text.name, fresh.text.text, fresh.text);
  }

  /** The composer took its draft. */
  takeDraft(id: string): string | null {
    const draft = this.state.drafts[id];
    if (draft === undefined) return null;
    const { [id]: _taken, ...drafts } = this.state.drafts;
    this.set({ drafts });
    return draft;
  }

  /**
   * The chat filtered to one resource (a server page's «Ассистент»): its
   * newest conversation opens with the list beside it, or a new one about it.
   */
  async openFiltered(
    filter: NonNullable<AssistantState["filter"]>,
    fresh: NewConversation,
  ): Promise<void> {
    this.set({ open: true, filter });
    await this.refreshList();
    const about = conversationsAbout(this.state.conversations, filter.kind, filter.resourceId);
    if (about[0]) {
      this.set({ view: "expanded" });
      this.select(about[0].id);
      return;
    }
    await this.start(fresh);
  }

  setFilter(filter: AssistantState["filter"]): void {
    this.set({ filter });
  }

  // --- Attachments ----------------------------------------------------------------------

  private setPending(id: string, change: (list: PendingAttachment[]) => PendingAttachment[]) {
    this.set({ pending: { ...this.state.pending, [id]: change(this.state.pending[id] ?? []) } });
  }

  private async upload(
    id: string,
    item: PendingAttachment,
    body: CreateAssistantAttachmentRequest,
  ): Promise<void> {
    this.setPending(id, (list) => [...list, item]);
    try {
      const attachment = await api(`/assistant/conversations/${id}/attachments`, {
        method: "POST",
        body,
        schema: AssistantAttachment,
      });
      this.setPending(id, (list) =>
        list.map((p) => (p.localId === item.localId ? { ...p, status: "ready", attachment } : p)),
      );
    } catch (error) {
      const problem =
        error instanceof ApiProblem && error.problem
          ? (error.problem.reason ?? error.problem.code)
          : "assistant_failed";
      this.setPending(id, (list) =>
        list.map((p) => (p.localId === item.localId ? { ...p, status: "failed", problem } : p)),
      );
    }
  }

  /** A file the person picked or pasted: a log or a screenshot. */
  async attachFile(id: string, file: File, source: "file" | "paste" = "file"): Promise<void> {
    const prepared = await prepareFile(file, source);
    const item: PendingAttachment = {
      localId: crypto.randomUUID(),
      name: prepared.name,
      kind: prepared.kind,
      status: "uploading",
      attachment: null,
      problem: null,
      preview: prepared.preview,
    };
    if ("problem" in prepared) {
      this.setPending(id, (list) => [
        ...list,
        { ...item, status: "failed", problem: prepared.problem },
      ]);
      return;
    }
    await this.upload(id, item, prepared.body);
  }

  /** Text as a log: a long paste, or a terminal's selection. */
  async attachText(
    id: string,
    name: string,
    text: string,
    from: { source: "paste" | "terminal"; serverId?: string } = { source: "paste" },
  ): Promise<void> {
    const item: PendingAttachment = {
      localId: crypto.randomUUID(),
      name,
      kind: "log",
      status: "uploading",
      attachment: null,
      problem: null,
      preview: null,
    };
    await this.upload(id, item, {
      kind: "log",
      source: from.source,
      name,
      text,
      ...(from.serverId ? { serverId: from.serverId } : {}),
    });
  }

  /** Drops an attachment from the composer (and from the API while it waits there). */
  async removePending(id: string, localId: string): Promise<void> {
    const item = (this.state.pending[id] ?? []).find((p) => p.localId === localId);
    this.setPending(id, (list) => list.filter((p) => p.localId !== localId));
    if (item?.preview) URL.revokeObjectURL(item.preview);
    if (item?.attachment)
      await api(`/assistant/conversations/${id}/attachments/${item.attachment.id}`, {
        method: "DELETE",
      }).catch(() => undefined);
  }

  async load(id: string): Promise<void> {
    const conversation = await api(`/assistant/conversations/${id}`, {
      schema: AssistantConversation,
    });
    const { messages, ...summary } = conversation;
    this.setSummary(summary);
    this.setThread(id, (thread) =>
      thread.running ? thread : { ...thread, messages, loaded: true },
    );
  }

  /**
   * Opens the chat on a page: the last conversation continues when the rule
   * allows (and the page's resource joins it), else a new one starts.
   */
  async openFor(page: PageInfo, fresh: NewConversation): Promise<void> {
    this.set({ open: true });
    await this.refreshList();
    const opening = whereToOpen(this.state.conversations, page.projectId, Date.now());
    if (opening.kind === "continue") {
      this.select(opening.id);
      if (fresh.chip) await this.attach(opening.id, fresh.chip);
      return;
    }
    await this.start(fresh);
  }

  async start(fresh: NewConversation): Promise<string> {
    const summary = await api("/assistant/conversations", {
      method: "POST",
      body: {
        projectId: fresh.projectId,
        title: fresh.title,
        ...(fresh.chip ? { context: [fresh.chip] } : {}),
      },
      schema: AssistantConversationSummary,
    });
    this.setSummary(summary);
    this.setThread(summary.id, () => ({ ...EMPTY_THREAD, loaded: true }));
    this.select(summary.id);
    return summary.id;
  }

  async attach(id: string, chip: AssistantContextInput): Promise<void> {
    const summary = await api(`/assistant/conversations/${id}/context`, {
      method: "POST",
      body: chip,
      schema: AssistantConversationSummary,
    });
    this.setSummary(summary);
  }

  async detach(id: string, chipId: string): Promise<void> {
    const summary = await api(`/assistant/conversations/${id}/context/${chipId}`, {
      method: "DELETE",
      schema: AssistantConversationSummary,
    });
    this.setSummary(summary);
  }

  async rename(id: string, title: string): Promise<void> {
    const summary = await api(`/assistant/conversations/${id}`, {
      method: "PATCH",
      body: { title },
      schema: AssistantConversationSummary,
    });
    this.setSummary(summary);
  }

  async remove(id: string): Promise<void> {
    await api(`/assistant/conversations/${id}`, { method: "DELETE" });
    const { [id]: _removed, ...threads } = this.state.threads;
    this.set({
      conversations: this.state.conversations.filter((c) => c.id !== id),
      threads,
      activeId: this.state.activeId === id ? null : this.state.activeId,
      unread: this.state.unread.filter((u) => u !== id),
    });
  }

  /**
   * Sends a message and follows the answer's stream to its end. `share` is
   * what goes along by itself at the terminal's access level: the focused
   * terminal's screen and the person's selection in it.
   */
  async send(
    id: string,
    text: string,
    page: AssistantPageContext,
    share: Share = NOTHING_SHARED,
  ): Promise<void> {
    this.setThread(id, (thread) => ({ ...thread, running: true, problem: null }));
    // The focused terminal goes as page context, never as an attachment (the API applies its level).
    const terminal = share.terminal ? await terminalContext(share.terminal) : null;
    const ready = (this.state.pending[id] ?? []).filter((p) => p.status === "ready");
    const attachments = ready.flatMap((p) => (p.attachment ? [p.attachment] : []));
    await this.follow(
      id,
      `/assistant/conversations/${id}/messages`,
      {
        text,
        page: { ...page, terminal },
        ...(attachments.length > 0 ? { attachments: attachments.map((a) => a.id) } : {}),
      },
      text,
      true,
      attachments,
      () => this.setPending(id, (list) => list.filter((p) => !ready.includes(p))),
    );
  }

  /**
   * «Выполнить» or «Отклонить» on a card: the card's progress and the
   * assistant's follow-up stream in like an answer. Answers the reason when
   * the API refused before anything ran (`assistant_change_mode_required`,
   * `step_up_required`, a wrong name), so the card can ask for it.
   */
  async decide(
    id: string,
    cardId: string,
    decision: "confirm" | "reject",
    body: { token: string; confirmName?: string } | null,
  ): Promise<string | null> {
    // The outcome's answer gives times in the person's zone and speaks their panel's language.
    const place = placeOfPerson();
    const refused = await this.follow(
      id,
      `/assistant/conversations/${id}/cards/${cardId}/${decision}`,
      body ? { ...body, place } : { place },
      "",
      false,
    );
    return refused?.reason ?? null;
  }

  /**
   * ▶ «Выполнить в терминале» of a command block of the answer `messageId`:
   * the API runs that block's exact text in the session as the assistant's
   * `terminal_run` (docs/assistant.md, "Terminal access") and streams the
   * answer about it; the conversation keeps «▶ Запуск по кнопке: …». Answers
   * `"missing"` when the API has no such route (404 without a reason of ours),
   * so the caller can ask in a message instead; a refusal shows as the thread's problem.
   */
  async runBlock(
    id: string,
    messageId: string,
    command: string,
    sessionId: string,
  ): Promise<"done" | "missing"> {
    const refused = await this.follow(
      id,
      `/assistant/conversations/${id}/messages/${messageId}/run`,
      { command, sessionId, place: placeOfPerson() },
      "",
      false,
    );
    if (!refused) return "done";
    if (refused.status === 404 && !RUN_REASONS.has(refused.reason)) return "missing";
    this.setThread(id, (thread) => ({ ...thread, problem: refused.reason }));
    return "done";
  }

  /** Posts and follows an answer's stream to its end; the reason when it was refused first. */
  private async follow(
    id: string,
    path: string,
    body: unknown,
    question: string,
    showProblem: boolean,
    attachments: AssistantMessage["attachments"] = [],
    /** The API took the message (the composer's attachments are sent then). */
    onAccepted?: () => void,
  ): Promise<{ reason: string; status: number } | null> {
    try {
      const response = await fetch(`${API_BASE}${path}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json", accept: "text/event-stream" },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      if (!response.ok || !response.body) {
        const problem = await reasonOf(response);
        this.setThread(id, (thread) => ({
          ...thread,
          running: false,
          problem: showProblem ? problem : thread.problem,
        }));
        return { reason: problem, status: response.status };
      }
      onAccepted?.();
      this.setThread(id, (thread) => ({ ...thread, running: true, problem: null }));
      this.markRunning(id, true);
      const parse = createSseParser((event) => this.receive(id, event, question, attachments));
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        parse(value);
      }
    } catch {
      // The connection broke (network, server restart): the answer may be stored; say so.
      this.setThread(id, (thread) => ({ ...thread, problem: "assistant_failed" }));
    } finally {
      this.setThread(id, (thread) => ({ ...thread, running: false }));
      this.markRunning(id, false);
    }
    return null;
  }

  /** The stop button: the API ends the model call and the tools; the stream then ends. */
  async stop(id: string): Promise<void> {
    await api(`/assistant/conversations/${id}/stop`, { method: "POST" });
  }

  private receive(
    id: string,
    event: AssistantStreamEvent,
    question: string,
    attachments: AssistantMessage["attachments"],
  ): void {
    if (event.type === "context") {
      const summary = this.state.conversations.find((c) => c.id === id);
      if (summary) this.setSummary(withChip(summary, event.item));
      return;
    }
    this.setThread(id, (thread) => applyEvent(thread, event, question, attachments));
    if (event.type === "done") {
      const seen = this.state.open && this.state.activeId === id;
      if (!seen && !this.state.unread.includes(id))
        this.set({ unread: [...this.state.unread, id] });
      void this.refreshList().catch(() => undefined);
    }
  }

  private markRunning(id: string, running: boolean): void {
    this.set({
      conversations: this.state.conversations.map((c) => (c.id === id ? { ...c, running } : c)),
    });
  }
}

/**
 * What goes along with a message by itself (`send`; docs/assistant.md,
 * "Screen context"): the focused SSH terminal of the conversation's
 * project — its session, and while the Autopilot may at least read it, its
 * screen and the person's selection there.
 */
export interface Share {
  terminal: {
    sessionId: string;
    /** The terminal's screen to read at send time; null below «Только чтение». */
    screen: Pick<Screen, "capture"> | null;
    /** The person's selection, unless they removed the quote; null below «Только чтение». */
    selection: string | null;
  } | null;
}

const NOTHING_SHARED: Share = { terminal: null };

/**
 * The terminal as the API takes it (`page.terminal`): the screen read now
 * (redacted, the newest characters kept) and the selection (redacted, cut).
 */
export async function terminalContext(
  terminal: NonNullable<Share["terminal"]>,
): Promise<AssistantTerminalContext> {
  const capture = await terminal.screen?.capture().catch(() => null);
  const screen = capture?.kind === "log" ? capture.text.slice(-ASSISTANT_TERMINAL_SCREEN_MAX) : "";
  const selection = terminal.selection
    ? redactText(terminal.selection).slice(0, ASSISTANT_TERMINAL_SELECTION_MAX)
    : "";
  return {
    sessionId: terminal.sessionId,
    ...(screen ? { screen } : {}),
    ...(selection.trim() ? { selection } : {}),
  };
}

/** The conversations with a chip of this resource, newest first. */
export function conversationsAbout(
  conversations: readonly AssistantConversationSummary[],
  kind: string,
  resourceId: string,
): AssistantConversationSummary[] {
  return conversations
    .filter((c) => c.context.some((chip) => chip.kind === kind && chip.resourceId === resourceId))
    .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
}

/** The page's one store. */
export const assistant = new AssistantStore();

export function useAssistant(): AssistantState {
  return useSyncExternalStore(assistant.subscribe, assistant.snapshot, assistant.snapshot);
}
